import type {
  AdminRouletteConfigProfile,
  AuthenticatedAdmin,
} from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type RouletteOperationsResponse } from "../api/client.js";
import { can } from "../capabilities.js";
import { ApprovedConfigProfiles } from "../components/ApprovedConfigProfiles.js";
import { OperatorProfiles } from "../components/OperatorProfiles.js";
import { asAmount, asDuration, asMoment } from "../format.js";

const REFRESH_MS = 10_000;

/** Read Roulette authority and activate only configurations in the certified catalog. */
export function RouletteOperations({ admin }: { admin: AuthenticatedAdmin }) {
  const [operatorProfilesEnabled, setOperatorProfilesEnabled] = useState(false);
  const [data, setData] = useState<RouletteOperationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);

  const load = useCallback(async (polling = false) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    try {
      setData(await api.rouletteOperations(controller.signal, polling));
      setError(null);
    } catch (caught) {
      if (!controller.signal.aborted) {
        setError(
          caught instanceof ApiError ? caught.message : "Could not load Roulette operations.",
        );
      }
    } finally {
      if (request.current === controller) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(true), REFRESH_MS);
    return () => {
      window.clearInterval(interval);
      request.current?.abort();
    };
  }, [load]);

  async function activateProfile(profile: AdminRouletteConfigProfile, reason: string) {
    if (!data?.table) throw new Error("Roulette authority is not available.");
    const table = data.table;
    const result = await api.activateRouletteConfigProfile({
      expectedActive: table.configuration.active,
      expectedPending: null,
      reason,
      tableId: table.tableId,
      target: {
        configId: profile.config.configId,
        configSha256: profile.config.configSha256,
      },
    });
    setSaved(
      result.outcome === "scheduled"
        ? `${profile.displayName} is pending the next safe table boundary.`
        : result.outcome === "already_active"
          ? `${profile.displayName} was already active.`
          : `${profile.displayName} is active.`,
    );
    await load();
  }

  async function cancelPendingProfile(reason: string) {
    if (!data?.table) throw new Error("Roulette authority is not available.");
    const table = data.table;
    const pending = table.configuration.pending;
    if (!pending) throw new Error("There is no pending Roulette profile to cancel.");
    await api.cancelRouletteConfigProfile({
      expectedActive: table.configuration.active,
      expectedPending: pending.target,
      reason,
      tableId: table.tableId,
    });
    const pendingName =
      table.configuration.approvedProfiles.find(
        (profile) =>
          profile.config.configId === pending.target.configId &&
          profile.config.configSha256 === pending.target.configSha256,
      )?.displayName ?? pending.target.configId;
    setSaved(`${pendingName} was removed from the activation queue.`);
    await load();
  }

  if (loading && !data) return <p className="empty">Loading Roulette operations…</p>;

  return (
    <section className="body poker-operations">
      <OperatorProfiles
        game="roulette"
        role={admin.role}
        onEnabled={setOperatorProfilesEnabled}
        onChanged={() => load()}
      />
      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}
      {saved ? (
        <p className="banner banner-quiet" role="status">
          {saved}
        </p>
      ) : null}

      {!data?.table ? (
        <div className="card">
          <p className="empty">
            {data?.enabled
              ? "Roulette authority is not available."
              : "Roulette is disabled in this environment."}
          </p>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="card-bar">
              <strong>{data.table.tableId}</strong>
              <span className={`badge badge-${data.status === "ready" ? "active" : "suspended"}`}>
                <span className="dot" />
                {data.status}
              </span>
            </div>
            <div className="facts">
              <div className="fact">
                <div className="fact-label">Active profile</div>
                <div className="fact-value">
                  {data.table.configuration.approvedProfiles.find(
                    (profile) =>
                      profile.config.configId === data.table?.configuration.active.configId &&
                      profile.config.configSha256 === data.table?.configuration.active.configSha256,
                  )?.displayName ?? data.table.configuration.active.configId}
                </div>
              </div>
              <div className="fact">
                <div className="fact-label">Pending profile</div>
                <div className="fact-value">
                  {data.table.configuration.pending?.target.configId ?? "None"}
                </div>
              </div>
              <div className="fact">
                <div className="fact-label">Phase</div>
                <div className="fact-value">{data.table.phase}</div>
              </div>
              <div className="fact">
                <div className="fact-label">Updated</div>
                <div className="fact-value">{asMoment(data.table.updatedAt)}</div>
              </div>
            </div>
          </div>
          <ApprovedConfigProfiles
            operatorProfilesEnabled={operatorProfilesEnabled}
            canWrite={
              can(admin.role, "games.config.activate") ||
              (operatorProfilesEnabled && admin.role === "admin")
            }
            configuration={data.table.configuration}
            gameName="Roulette"
            onActivate={activateProfile}
            onCancelPending={cancelPendingProfile}
            renderSettings={(profile) => (
              <>
                <div>
                  <dt>Betting</dt>
                  <dd>{asDuration(profile.config.bettingDurationMs)}</dd>
                </div>
                <div>
                  <dt>Result display</dt>
                  <dd>{asDuration(profile.config.resultDisplayDurationMs)}</dd>
                </div>
                <div>
                  <dt>Presentation</dt>
                  <dd>{profile.config.presentationMode}</dd>
                </div>
                <div>
                  <dt>Minimum bet</dt>
                  <dd>
                    {asAmount({
                      amount: profile.config.minimumPositionBetMinor,
                      currency: profile.config.currency.code,
                    })}
                  </dd>
                </div>
                <div>
                  <dt>Aggregate maxima</dt>
                  <dd>
                    {asAmount({
                      amount: profile.config.aggregateInsideMaxMinor,
                      currency: profile.config.currency.code,
                    })}
                    {" inside / "}
                    {asAmount({
                      amount: profile.config.aggregateOutsideMaxMinor,
                      currency: profile.config.currency.code,
                    })}
                    {" outside"}
                  </dd>
                </div>
                <div>
                  <dt>Chips</dt>
                  <dd>
                    {profile.config.chipDenominationsMinor
                      .map((amount) => asAmount({ amount, currency: profile.config.currency.code }))
                      .join(", ")}
                  </dd>
                </div>
              </>
            )}
          />
        </>
      )}
      <button className="btn btn-quiet" onClick={() => void load()} type="button">
        Refresh
      </button>
    </section>
  );
}
